import * as React from "react";
import { useRef } from "react";
import { WorkflowNode } from "../../types";
import { FormGroup, ToggleGroup } from "../RightAsideHelpers";
import Input from "@/components/Input";
import TextArea from "@/components/TextArea";
import VariablePicker from "@/components/VariablePicker";
import VariableHelpCard from "@/components/VariableHelpCard";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function SendEmailEditor({ node, onUpdate }: Props) {
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  function insertIntoBody(variable: string) {
    const el = bodyRef.current;
    if (el) {
      const start = el.selectionStart ?? (node.emailBody?.length || 0);
      const end = el.selectionEnd ?? start;
      const current = node.emailBody || "";
      const newText =
        current.substring(0, start) + variable + current.substring(end);
      onUpdate({ emailBody: newText });
      requestAnimationFrame(() => {
        el.focus();
        const newPos = start + variable.length;
        el.setSelectionRange(newPos, newPos);
      });
    } else {
      onUpdate({ emailBody: (node.emailBody || "") + variable });
    }
  }

  return (
    <>
      <VariableHelpCard context="sendEmail" />
      <FormGroup label="Mail To">
        <Input
          type="text"
          value={node.emailTo || ""}
          onChange={(emailTo) => onUpdate({ emailTo })}
          suffixIcon={
            <VariablePicker
              onInsert={(v) => onUpdate({ emailTo: (node.emailTo || "") + v })}
            />
          }
        />
      </FormGroup>
      <ToggleGroup
        toggled={node.sendTranscript || false}
        label="Send Conversation Transcript"
        changeToggle={() => {
          onUpdate({ sendTranscript: !node.sendTranscript });
        }}
      />

      {!node.sendTranscript && (
        <>
          <FormGroup label="Email Subject">
            <Input
              type="text"
              value={node.emailSubject || ""}
              onChange={(emailSubject) => onUpdate({ emailSubject })}
              suffixIcon={
                <VariablePicker
                  onInsert={(v) =>
                    onUpdate({
                      emailSubject: (node.emailSubject || "") + v
                    })
                  }
                />
              }
            />
          </FormGroup>
          <FormGroup label="Email Body">
            <div className="relative">
              <div className="absolute right-1 top-1 z-10">
                <VariablePicker onInsert={insertIntoBody} />
              </div>
              <TextArea
                ref={bodyRef}
                value={node.emailBody || ""}
                onChange={(e) =>
                  onUpdate({ emailBody: e.currentTarget.value })
                }
              />
            </div>
          </FormGroup>
        </>
      )}
    </>
  );
}
